'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'

import { readBackendQueryParam, switchBackend } from '~/configs/backendSwitch'
import {
  BACKEND_QUERY_PARAM,
  BACKEND_STORAGE_KEY,
  activeBackend,
  allowsRuntimeBackendSwitch,
  defaultBackend
} from '~/configs/env'

/**
 * Client-only side effects related to backend selection. Renders nothing.
 *
 *  1. If a `?backend=<key>` query param is present and points to a different
 *     backend than the currently-active one, apply it via `switchBackend`
 *     (which clears storage and reloads). If it's missing, sync it to the
 *     active backend via `replaceState` so the URL is always shareable. Runs
 *     on every client-side navigation, since `router.push` and `<Link>` strip
 *     the search string when navigating to a path that doesn't carry it.
 *  2. If no value is persisted yet (first visit), write `defaultBackend` to
 *     localStorage so the UI always reflects a concrete persisted value.
 *
 * Both effects are no-ops outside the `preview` build profile.
 */
export default function BackendBootstrap() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (!allowsRuntimeBackendSwitch) return

    const fromQuery = readBackendQueryParam()
    if (fromQuery) {
      if (fromQuery !== activeBackend) {
        void switchBackend(fromQuery)
        return
      }
    } else {
      const url = new URL(window.location.href)
      url.searchParams.set(BACKEND_QUERY_PARAM, activeBackend)
      window.history.replaceState(null, '', url.toString())
    }

    try {
      if (!window.localStorage.getItem(BACKEND_STORAGE_KEY)) {
        window.localStorage.setItem(BACKEND_STORAGE_KEY, defaultBackend)
      }
    } catch {
      // localStorage unavailable — no-op
    }
  }, [pathname, searchParams])

  return null
}
